import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { loadGoogleMaps } from "shared/lib/google-maps-loader";
import {
  parseGooglePlace,
  type ParsedPlaceAddress,
} from "shared/lib/parse-google-place";

export type { ParsedPlaceAddress };

export interface AddressAutocompleteProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> {
  onChange: (value: string) => void;
  onPlaceSelect: (place: ParsedPlaceAddress) => void;
  onManualEdit?: () => void;
  /** ISO 3166-1 alpha-2 country codes, e.g. `ng` */
  countries?: string[];
}

const AddressAutocomplete = forwardRef<
  HTMLInputElement,
  AddressAutocompleteProps
>(
  (
    {
      onChange,
      onPlaceSelect,
      onManualEdit,
      countries = [], // Add NG later to limit to Nigeria
      className = "",
      onBlur,
      defaultValue,
      ...inputProps
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const selectingPlaceRef = useRef(false);
    const onChangeRef = useRef(onChange);
    const onPlaceSelectRef = useRef(onPlaceSelect);
    const onManualEditRef = useRef(onManualEdit);

    onChangeRef.current = onChange;
    onPlaceSelectRef.current = onPlaceSelect;
    onManualEditRef.current = onManualEdit;

    useImperativeHandle(
      forwardedRef,
      () => inputRef.current as HTMLInputElement,
    );

    useEffect(() => {
      const input = inputRef.current;
      if (!input) return;

      let autocomplete: google.maps.places.Autocomplete | null = null;
      let listener: google.maps.MapsEventListener | null = null;
      let cancelled = false;

      loadGoogleMaps()
        .then((google) => {
          if (cancelled) return;
          autocomplete = new google.maps.places.Autocomplete(input, {
            fields: ["address_components", "geometry", "formatted_address"],
            componentRestrictions: countries.length
              ? { country: countries }
              : undefined,
          });
          listener = autocomplete.addListener("place_changed", () => {
            const place = autocomplete?.getPlace();
            if (!place) return;
            const parsed = parseGooglePlace(place);
            if (!parsed) return;

            selectingPlaceRef.current = true;
            input.value = parsed.formattedAddress;
            onPlaceSelectRef.current(parsed);
            onChangeRef.current(parsed.formattedAddress);
            selectingPlaceRef.current = false;
          });
        })
        .catch(() => {
          /* Plain text input still works without API key */
        });

      return () => {
        cancelled = true;
        listener?.remove();
      };
    }, [countries]);

    return (
      <input
        {...inputProps}
        ref={inputRef}
        defaultValue={defaultValue}
        autoComplete="off"
        className={className}
        onChange={(e) => {
          if (!selectingPlaceRef.current) {
            onManualEditRef.current?.();
          }
          onChangeRef.current(e.target.value);
        }}
        onBlur={onBlur}
      />
    );
  },
);

AddressAutocomplete.displayName = "AddressAutocomplete";

export default AddressAutocomplete;
